import axios from 'axios'
import hash from 'object-hash'

import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { Palette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const PERCENTUAL_Y_AXIS = { id: 'percentual', labelSuffix: '%', opposite: true }

export namespace Insurance {
  export namespace Bbse {
    const CONTEXT_ID = 'equity:insurance:bbse'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'lucro'
        | 'lucro_3m'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'desp_comercializacao':
            return getDespComercializacaoChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getYAxisTitle = (chart: ChartConfig['chart']) => {
      switch (chart) {
        case 'margem_contribuicao':
          return { abs: 'Margem de Contribuição', perc: 'YoY e Razão' }
        case 'margem_contribuicao_3m':
          return { abs: 'Margem de Contribuição 3M', perc: 'YoY e Razão' }
        case 'lucro':
          return { abs: 'Lucro 3M', perc: 'YoY e Margem Líquida' }
        case 'lucro_3m':
          return { abs: 'Lucro', perc: 'Crescimento YoY' }
        default:
          return {}
      }
    }

    const getDespComercializacaoChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        tooltip: { xDateFormat: '%B %Y' },
      }
    )

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const { abs, perc } = getYAxisTitle(config.chart)
      return getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          addMinMaxIndicatorsIndexes: [1],
          convertToPercentages: [false, true],
          tooltip: { xDateFormat: '%B %Y' },
          yAxis: [
            { id: 'absolute', title: { text: abs } },
            { ...PERCENTUAL_Y_AXIS, title: { text: perc } },
          ],
        },
        {
          color: [Palette.fadedBlue, Palette.darkBlue],
          type: ['column', 'line'],
          yAxis: ['absolute', 'percentual'],
        }
      )(config, data)
    }
  }

  export namespace Sh1 {
    const CONTEXT_ID = 'equity:insurance:sh1'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'premio_ganho'
        | 'premio_ganho_3m'
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'resultado_resseguro'
        | 'lucro'
        | 'lucro_3m'
        | 'resultado_financeiro'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'premio_ganho':
          case 'premio_ganho_3m':
          case 'resultado_financeiro':
            return getTwoSeriesChartProps(config, data)
          case 'desp_comercializacao':
          case 'resultado_resseguro':
            return getMultipleLineChartProps(Context, {
              tooltip: { xDateFormat: '%B %Y' },
            })(config, data)
          case 'margem_contribuicao':
          case 'margem_contribuicao_3m':
          case 'lucro':
          case 'lucro_3m':
            return getThreeSeriesChartProps(config, data)
        }
      },
    }

    const getYAxisTitle = (chart: ChartConfig['chart']) => {
      switch (chart) {
        case 'premio_ganho':
          return { abs: 'Prêmio Ganho', perc: 'Prêmio Ganho YoY' }
        case 'premio_ganho_3m':
          return { abs: 'Prêmio Ganho 3M', perc: 'Prêmio Ganho 3M YoY' }
        case 'margem_contribuicao':
          return { abs: 'Margem de Contribuição', perc: 'YoY e Razão' }
        case 'margem_contribuicao_3m':
          return { abs: 'Margem de Contribuição 3M', perc: 'YoY e Razão' }
        case 'lucro':
          return { abs: 'Lucro', perc: 'YoY e Margem Líquida' }
        case 'lucro_3m':
          return { abs: 'Lucro', perc: 'Crescimento YoY' }
        case 'resultado_financeiro':
          return { abs: 'Valor Mensal', perc: 'Valor YoY' }
        default:
          return {}
      }
    }

    const getTwoSeriesChartProps = (config: ChartConfig, data: ChartData) => {
      const { abs, perc } = getYAxisTitle(config.chart)
      return getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          addMinMaxIndicatorsIndexes: [1],
          convertToPercentages: [false, true],
          tooltip: { xDateFormat: '%B %Y' },
          yAxis: [
            { id: 'absolute', title: { text: abs } },
            { ...PERCENTUAL_Y_AXIS, title: { text: perc } },
          ],
        },
        {
          color: [Palette.fadedBlue, Palette.darkBlue],
          tooltip: ['', '%'].map((unit) => ({
            pointFormatter: magnitudePointFormatter({ unit }),
          })),
          type: ['column', 'line'],
          yAxis: ['absolute', 'percentual'],
        }
      )(config, data)
    }

    const getThreeSeriesChartProps = (config: ChartConfig, data: ChartData) => {
      const { abs, perc } = getYAxisTitle(config.chart)
      return getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          addMinMaxIndicatorsIndexes: [1],
          convertToPercentages: [false, true, true],
          tooltip: { xDateFormat: '%B %Y' },
          yAxis: [
            { id: 'absolute', title: { text: abs } },
            { ...PERCENTUAL_Y_AXIS, title: { text: perc } },
            { id: 'percentual2', labelSuffix: '%' },
          ],
        },
        {
          color: [Palette.fadedBlue, Palette.darkBlue, Palette.darkGreen],
          tooltip: ['', '%', '%'].map((unit) => ({
            pointFormatter: magnitudePointFormatter({ unit }),
          })),
          type: ['column', 'line', 'line'],
          yAxis: ['absolute', 'percentual', 'percentual2'],
        }
      )(config, data)
    }
  }

  export namespace Brasilcap {
    const CONTEXT_ID = 'equity:insurance:brasilcap'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'arrecadacao'
        | 'arrecadacao_3m'
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'resultado_financeiro'
        | 'lucro'
        | 'lucro_3m'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'arrecadacao':
          case 'arrecadacao_3m':
            return getColumnLineChartProps(config, data)
          case 'desp_comercializacao':
            return getMultipleLineChartProps(Context, {
              tooltip: { xDateFormat: '%B %Y' },
            })(config, data)
          case 'margem_contribuicao':
          case 'margem_contribuicao_3m':
          case 'lucro':
          case 'lucro_3m':
            return getColumnLineLineChartProps(config, data)
          case 'resultado_financeiro':
            return getResultadoFinanceiroChartProps(config, data)
        }
      },
    }

    const getColumnLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )

    const getColumnLineLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute' },
          PERCENTUAL_Y_AXIS,
          { id: 'percentual2', labelSuffix: '%' },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue, Palette.darkGreen],
        tooltip: ['', '%', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line', 'line'],
        yAxis: ['absolute', 'percentual', 'percentual2'],
      }
    )

    const getResultadoFinanceiroChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['line', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )
  }

  export namespace Brasilprev {
    const CONTEXT_ID = 'equity:insurance:brasilprev'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'contribuicao'
        | 'contribuicao_3m'
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'resultado_financeiro'
        | 'lucro'
        | 'lucro_3m'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'contribuicao':
          case 'contribuicao_3m':
            return getColumnLineChartProps(config, data)
          case 'desp_comercializacao':
            return getMultipleLineChartProps(Context, {
              tooltip: { xDateFormat: '%B %Y' },
            })(config, data)
          case 'margem_contribuicao':
          case 'margem_contribuicao_3m':
          case 'lucro':
          case 'lucro_3m':
            return getColumnLineLineChartProps(config, data)
          case 'resultado_financeiro':
            return getResultadoFinanceiroChartProps(config, data)
        }
      },
    }

    const getColumnLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )

    const getColumnLineLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute' },
          PERCENTUAL_Y_AXIS,
          { id: 'percentual2', labelSuffix: '%' },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue, Palette.darkGreen],
        tooltip: ['', '%', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line', 'line'],
        yAxis: ['absolute', 'percentual', 'percentual2'],
      }
    )

    const getResultadoFinanceiroChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['line', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )
  }

  export namespace Caixa {
    const CONTEXT_ID = 'equity:insurance:caixa'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'premio_ganho'
        | 'premio_ganho_3m'
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'resultado_resseguro'
        | 'lucro'
        | 'lucro_3m'
        | 'resultado_financeiro'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'premio_ganho':
          case 'premio_ganho_3m':
            return getLineChartProps(config, data)
          case 'desp_comercializacao':
          case 'resultado_resseguro':
            return getMultipleLineChartProps(Context, {
              tooltip: { xDateFormat: '%B %Y' },
            })(config, data)
          case 'margem_contribuicao':
          case 'margem_contribuicao_3m':
          case 'lucro':
          case 'lucro_3m':
            return getColumnLineLineChartProps(config, data)
          case 'resultado_financeiro':
            return getResultadoFinanceiroChartProps(config, data)
        }
      },
    }

    const getLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )

    const getColumnLineLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute' },
          PERCENTUAL_Y_AXIS,
          { id: 'percentual2', labelSuffix: '%' },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue, Palette.darkGreen],
        tooltip: ['', '%', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line', 'line'],
        yAxis: ['absolute', 'percentual', 'percentual2'],
      }
    )

    const getResultadoFinanceiroChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [{ id: 'absolute' }, PERCENTUAL_Y_AXIS],
      },
      {
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['line', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )
  }

  export namespace Irb {
    const CONTEXT_ID = 'equity:insurance:irb'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'premio_ganho'
        | 'premio_ganho_3m'
        | 'resseguro_premio_ganho'
        | 'sinistralidade'
        | 'resseguro_sinistralidade'
        | 'desp_comercializacao'
        | 'margem_contribuicao'
        | 'margem_contribuicao_3m'
        | 'lucro'
        | 'lucro_3m'
        | 'resultado_retrocessao'
        | 'resultado_retrocessao_margem'
        | 'resultado_financeiro'
        | 'resultado_financeiro_margem'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'premio_ganho':
            return getPremioGanhoProps(config, data)
          case 'premio_ganho_3m':
            return getPremioGanho3MChartProps(config, data)
          case 'resseguro_premio_ganho':
          case 'resseguro_sinistralidade':
          case 'desp_comercializacao':
          case 'sinistralidade':
          case 'resultado_retrocessao_margem':
          case 'resultado_financeiro_margem':
            return getChartProps(config, data)
          case 'margem_contribuicao':
          case 'margem_contribuicao_3m':
          case 'lucro':
          case 'lucro_3m':
            return getColumnLineLineChartProps(config, data)
          case 'resultado_retrocessao':
          case 'resultado_financeiro':
            return getColumnLinePercentualChartProps(config, data)
        }
      },
    }

    const getPremioGanhoProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute', title: { text: 'Prêmio Ganho' } },
          { ...PERCENTUAL_Y_AXIS, title: { text: 'Premío Ganho YoY' } },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )

    const getPremioGanho3MChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute', title: { text: 'Prêmio Ganho 3M' } },
          { ...PERCENTUAL_Y_AXIS, title: { text: 'Premío Ganho YoY 3M' } },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        tooltip: ['', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line'],
        yAxis: ['absolute', 'percentual'],
      }
    )

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        ...(config.chart === 'sinistralidade' ||
        config.chart === 'resultado_retrocessao_margem' ||
        config.chart === 'resultado_financeiro_margem'
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        tooltip: {
          ...(config.chart === 'resseguro_premio_ganho'
            ? { pointFormatter: magnitudePointFormatter() }
            : config.chart === 'resultado_financeiro_margem'
            ? {
                pointFormatter: magnitudePointFormatter({
                  unit: '%',
                  decimals: 10,
                }),
              }
            : {}),
          xDateFormat: '%B %Y',
        },
      })(config, data)

    const getColumnLineLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        addMinMaxIndicatorsIndexes: [1],
        convertToPercentages: [false, true, true],
        tooltip: { xDateFormat: '%B %Y' },
        yAxis: [
          { id: 'absolute' },
          PERCENTUAL_Y_AXIS,
          { id: 'percentual2', labelSuffix: '%' },
        ],
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue, Palette.darkGreen],
        tooltip: ['', '%', '%'].map((unit) => ({
          pointFormatter: magnitudePointFormatter({ unit }),
        })),
        type: ['column', 'line', 'line'],
        yAxis: ['absolute', 'percentual', 'percentual2'],
      }
    )

    const getColumnLinePercentualChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true, true],
        tooltip: { xDateFormat: '%B %Y' },
      },
      {
        color: [Palette.fadedBlue, Palette.darkBlue],
        type: ['column', 'line'],
      }
    )
  }

  export namespace RouboFurto {
    const CONTEXT_ID = 'equity:insurance:roubo_furto'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rj_mensal' | 'rj_trimestral' | 'sp_mensal' | 'sp_trimestral'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          convertToPercentages: [true],
          tooltip: { xDateFormat: '%B %Y' },
        })(config, data),
    }
  }

  export namespace Ramos {
    const CONTEXT_ID = 'equity:insurance:ramos'

    type RamosChartId =
      | 'premio_ganho_ramos'
      | 'premio_ganho_ramos_3m'
      | 'sinistralidade_ramos'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title: string
      chart: RamosChartId
      option: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const RAMOS_CHART_PATHS: Record<RamosChartId, string> = {
      premio_ganho_ramos: '/premio-ganho-ramos',
      premio_ganho_ramos_3m: '/premio-ganho-ramos-3m',
      sinistralidade_ramos: '/sinistralidade-ramos',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: '/api/charts/insurance/sh1' + RAMOS_CHART_PATHS[config.chart],
        method: 'GET',
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isSelected = (seriesName: string, option: string) =>
        seriesName.includes(option) ||
        seriesName === 'Sinistralidade' ||
        seriesName === 'Sinistralidade 3m'

      const filterData = (data: ChartData, option: string) => ({
        ...data,
        data: data.data.filter((s) => isSelected(s.name, option)),
      })

      const isSinistralidadeRamos = config.chart === 'sinistralidade_ramos'

      const convertToPercentages = [
        isSinistralidadeRamos,
        ...Array(data.data.length - 1).fill(true),
      ]

      const yAxis = isSinistralidadeRamos
        ? [{ id: 'absolute', title: { text: '' } }]
        : [
            {
              id: 'absolute',
              opposite: false,
              title: { text: data ? data.data[0].name : '' },
            },
            {
              id: 'relative',
              opposite: true,
              title: { text: data ? data.data[1].name : '' },
              labelSuffix: '%',
            },
          ]

      return getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          convertToPercentages,
          yAxis,
          xAxis: datetimeXAxis[0],
        },
        {
          type: convertToPercentages.map((item) => (item ? 'line' : 'column')),
          yAxis: convertToPercentages.map((item) =>
            isSinistralidadeRamos ? 'absolute' : item ? 'relative' : 'absolute'
          ),
          color: !isSinistralidadeRamos
            ? [
                Palette.fadedBlue,
                ...Array(data.data.length - 1).fill(Palette.darkBlue),
              ]
            : undefined,
        }
      )(config, filterData(data, config.option))
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
